/**
 * Centralized Permission System
 * 
 * This module provides a unified way to handle permission checks throughout the application.
 * It defines permission groups and provides utility functions for checking access rights.
 */

import { Authorization, SelectedProject } from './Authorization.interfaces';

// Permission Groups Definition
export const PERMISSION_GROUPS = {
  // System-level permissions
  SYSTEM_ADMIN: 'system_admin',
  
  // Project-level permissions
  SWCA_PROJECT_MANAGER: 'swca_project_manager',

  // Combined permissions (most commonly used)
  ADMIN_OR_MANAGER: 'admin_or_manager',
  SWCA_USER: 'swca_user',

  // Data Review specific permissions
  DATA_REVIEW_PM: 'data_review_pm',           // sysAdmin OR (swca_user AND project manager)
  DATA_REVIEW_SWCA_NON_PM: 'data_review_swca_non_pm', // swca_user AND not manager AND not sysAdmin
  DATA_REVIEW_AGENCY: 'data_review_agency',   // not swca_user AND not sysAdmin

} as const;

export type PermissionGroup = typeof PERMISSION_GROUPS[keyof typeof PERMISSION_GROUPS];

/**
 * Core permission checking functions
 */
export const PermissionCheckers = {
  isSystemAdmin: (authorization: Authorization | null): boolean => {
    return authorization?.system_role === 'admin';
  },

  // Project-specific permission checks - require selectedProject context
  isProjectManager: (authorization: Authorization | null, selectedProject?: SelectedProject | null): boolean => {
    // If no project is selected, user cannot have project-specific permissions
    if (!selectedProject || !authorization?.projects) return false;
    return authorization.projects.some(p => p.projectId === selectedProject.projectId && p.role === 'manager');
  },

  isSWCAUser: (authorization: Authorization | null, selectedProject?: SelectedProject | null): boolean => {
    // If no project is selected, user cannot have project-specific permissions
    if (!selectedProject || !authorization?.projects) return false;
    return authorization.projects.some(p => p.projectId === selectedProject.projectId && authorization.swca_user);
  },

  // Combined permission checks
  isAdminOrManager: (authorization: Authorization | null, selectedProject?: SelectedProject | null): boolean => {
    return PermissionCheckers.isSystemAdmin(authorization) || 
           PermissionCheckers.isProjectManager(authorization, selectedProject);
  },

  // Data Review: PM = sysAdmin OR (swca_user AND project role is manager)
  isDataReviewPM: (authorization: Authorization | null, selectedProject?: SelectedProject | null): boolean => {
    return PermissionCheckers.isSystemAdmin(authorization) ||
      (!!authorization?.swca_user && PermissionCheckers.isProjectManager(authorization, selectedProject));
  },

  // Data Review: SWCA non-PM = swca_user AND not sysAdmin AND not manager for the project
  isDataReviewSWCANonPM: (authorization: Authorization | null, selectedProject?: SelectedProject | null): boolean => {
    return !PermissionCheckers.isSystemAdmin(authorization) &&
      !!authorization?.swca_user &&
      !PermissionCheckers.isProjectManager(authorization, selectedProject);
  },

  // Data Review: Agency user = not swca_user AND not sysAdmin
  isDataReviewAgency: (authorization: Authorization | null): boolean => {
    return !PermissionCheckers.isSystemAdmin(authorization) && !authorization?.swca_user;
  },
};

/**
 * Main permission checking function
 * @param authorization - User's authorization object
 * @param permissionGroup - The permission group to check
 * @param selectedProject - Currently selected project (optional, for project-specific checks)
 * @returns boolean indicating if user has the required permission
 */
export const hasPermission = (
  authorization: Authorization | null,
  permissionGroup: PermissionGroup,
  selectedProject?: SelectedProject | null
): boolean => {
  switch (permissionGroup) {
    case PERMISSION_GROUPS.SYSTEM_ADMIN:
      return PermissionCheckers.isSystemAdmin(authorization);

    case PERMISSION_GROUPS.SWCA_PROJECT_MANAGER:
      return PermissionCheckers.isProjectManager(authorization, selectedProject);

    case PERMISSION_GROUPS.ADMIN_OR_MANAGER:
      return PermissionCheckers.isAdminOrManager(authorization, selectedProject);

    case PERMISSION_GROUPS.SWCA_USER:
      return PermissionCheckers.isSWCAUser(authorization, selectedProject);

    case PERMISSION_GROUPS.DATA_REVIEW_PM:
      return PermissionCheckers.isDataReviewPM(authorization, selectedProject);

    case PERMISSION_GROUPS.DATA_REVIEW_SWCA_NON_PM:
      return PermissionCheckers.isDataReviewSWCANonPM(authorization, selectedProject);

    case PERMISSION_GROUPS.DATA_REVIEW_AGENCY:
      return PermissionCheckers.isDataReviewAgency(authorization);

    default:
      return false;
  }
};

/**
 * Permission configuration for different features/routes
 */
export const FEATURE_PERMISSIONS = {
  // Routes
  PROJECTS: PERMISSION_GROUPS.SYSTEM_ADMIN,
  USERS: PERMISSION_GROUPS.ADMIN_OR_MANAGER,
  USER_PROJECTS: PERMISSION_GROUPS.ADMIN_OR_MANAGER,
  DATA_REVIEW: PERMISSION_GROUPS.SWCA_USER,
  ADMINISTRATION: PERMISSION_GROUPS.ADMIN_OR_MANAGER,

  // Components
  DELETE_USER: PERMISSION_GROUPS.SYSTEM_ADMIN,
  ASSIGN_SYSTEM_ROLE: PERMISSION_GROUPS.SYSTEM_ADMIN,
  
} as const;

/**
 * Helper function to check feature permissions
 */
export const hasFeatureAccess = (
  authorization: Authorization | null,
  feature: keyof typeof FEATURE_PERMISSIONS,
  selectedProject?: SelectedProject | null
): boolean => {
  const requiredPermission = FEATURE_PERMISSIONS[feature];
  return hasPermission(authorization, requiredPermission, selectedProject);
};
